import { MeshBasicMaterial, Mesh, Group } from "three";
import { Vector3 } from "flavr-math";
import { mainScene } from "../rendering/mainScene";
import { RenderOrder } from "../rendering/RenderOrder";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { DEFAULT_PORTRAIT_IMAGE_RELATIVE_PATH, portraitTextureService } from "../rendering/texture/PortraitTextureService";
import { textureLoaderService } from "../rendering/texture/TextureLoaderService";
import { meshLoaderService } from "../rendering/mesh/MeshLoaderService";
const SUBMESHNAME_PHOTODISK = "PhotoDisk";
const SUBMESHNAME_OUTERARROW = "OuterArrow";
const SUBMESHNAME_INNERARROW = "InnerArrow";
/**
 * Changes vector 'a' so that it is orthogonal to vector 'b' and has length '1';
 * Vector 'b' must have a length of 1;
 */
function orthonormaliseVectors(a, b) {
    return a.addScaledVector(b, -a.dot(b)).normalize();
}
function getSubmeshGeometry(mesh, submeshName) {
    const submesh = mesh.getObjectByName(submeshName);
    if (!submesh || !(submesh instanceof Mesh))
        return undefined;
    return submesh.geometry;
}
const MARKER_GEOMETRY = meshLoaderService.loadOBJ("meshes/uiMeshes/AvatarMarker_A_07.obj").then((mesh) => {
    const photoDisk = getSubmeshGeometry(mesh, SUBMESHNAME_PHOTODISK);
    const outerArrow = getSubmeshGeometry(mesh, SUBMESHNAME_OUTERARROW);
    const innerArrow = getSubmeshGeometry(mesh, SUBMESHNAME_INNERARROW);
    if (!photoDisk || !outerArrow || !innerArrow) {
        throw new Error("MarkerMesh is missing SubMeshs");
    }
    return { photoDisk, outerArrow, innerArrow };
});
const tmpVector3_1 = new Vector3();
const tmpVector3_2 = new Vector3();
const tmpVector3_3 = new Vector3();
const tmpHSL = { h: 0, s: 0, l: 0 };
function desaturatedColor(color) {
    const l = color.getHSL(tmpHSL).l;
    color.setHSL(0, 0, l).getHex();
}
export class MarkerMesh {
    constructor(msg) {
        this.baseColor = msg.baseColor;
        this.outerArrowMaterial = this.createArrowMaterial(0xffffff);
        this.innerArrowMaterial = this.createArrowMaterial(msg.baseColor);
        this.photoDiskMaterial = this.createPhotoDiskMaterial(msg);
        this.arrow = new Group();
        this.arrow.matrixAutoUpdate = false;
        mainScene.add(this.arrow);
        MARKER_GEOMETRY.then((geom) => {
            this.photo = this.createMesh(this.photoDiskMaterial, geom.photoDisk, RenderOrder.OPAQUE_GEOMETRY);
            this.photo.visible = this.arrow.visible;
            mainScene.add(this.photo);
            const outerArrow = this.createMesh(this.outerArrowMaterial, geom.outerArrow, RenderOrder.OPAQUE_GEOMETRY);
            const innerArrow = this.createMesh(this.innerArrowMaterial, geom.innerArrow, RenderOrder.OPAQUE_GEOMETRY);
            this.arrow.add(outerArrow, innerArrow);
        });
    }
    delete() {
        mainScene.remove(this.arrow);
        if (this.photo)
            mainScene.remove(this.photo);
    }
    get visible() {
        return this.arrow.visible;
    }
    set visible(visible) {
        this.arrow.visible = visible;
        if (this.photo)
            this.photo.visible = visible;
    }
    changeColor(baseColor, useDesaturatedColors) {
        this.baseColor = baseColor;
        this.updateColors(useDesaturatedColors);
    }
    updateColors(useDesaturatedColors) {
        if (useDesaturatedColors) {
            desaturatedColor(this.outerArrowMaterial.color);
            desaturatedColor(this.innerArrowMaterial.color);
        }
        else {
            this.outerArrowMaterial.color.setHex(0xffffff);
            this.innerArrowMaterial.color.setHex(this.baseColor);
        }
    }
    updateTransform(position, direction, normal, scale, prioritizeDirectionOverNormal = false, useCameraUpForPhotoUp = true) {
        if (!this.photo)
            return;
        const zAxis = tmpVector3_1.copy(normal).multiplyScalar(-1);
        const yAxis = tmpVector3_2.copy(direction);
        if (prioritizeDirectionOverNormal) {
            orthonormaliseVectors(zAxis, yAxis);
        }
        else {
            orthonormaliseVectors(yAxis, zAxis);
        }
        const xAxis = tmpVector3_3.copy(yAxis).cross(zAxis).normalize();
        xAxis.multiplyScalar(scale);
        yAxis.multiplyScalar(scale);
        zAxis.multiplyScalar(scale);
        this.arrow.matrix.makeBasis(xAxis, yAxis, zAxis).setPosition(position);
        this.arrow.updateMatrixWorld(true);
        xAxis
            .copy(useCameraUpForPhotoUp ? localPlayerContainer.cameraWorldUpDirection : yAxis.set(0, 1, 0))
            .cross(zAxis)
            .normalize()
            .multiplyScalar(scale);
        yAxis.copy(zAxis).cross(xAxis).normalize().multiplyScalar(scale);
        this.photo.matrix.makeBasis(xAxis, yAxis, zAxis).setPosition(position);
        this.photo.updateMatrixWorld(true);
    }
    createArrowMaterial(color) {
        return new MeshBasicMaterial({
            color,
            depthTest: true,
            transparent: false
        });
    }
    createPhotoDiskMaterial(msg) {
        const markerTextureUrl = msg.avataNameOrTextureUrl.trim() === "" ? DEFAULT_PORTRAIT_IMAGE_RELATIVE_PATH : msg.avataNameOrTextureUrl;
        const texture = msg.isAvatarName
            ? portraitTextureService.getUserTexture(markerTextureUrl)
            : textureLoaderService.loadTexture(markerTextureUrl);
        return new MeshBasicMaterial({
            color: 0xffffff,
            map: texture,
            depthTest: true,
            transparent: true
        });
    }
    createMesh(mat, geom, renderOrder) {
        const mesh = new Mesh(geom, mat);
        mesh.renderOrder = renderOrder;
        mesh.matrixAutoUpdate = false;
        mesh.frustumCulled = false;
        return mesh;
    }
}
